import {
    optionsFlex_searchPlaceholder,
    optionsFlex_goToFullOptions,
} from './QuickOptions.locstring.json';
import { optionsFull_search_noResults } from '../../strings.locstring.json';
import loc from 'owa-localize';
import QuickOptionEntry from './QuickOptionEntry';
import QuickOptionSearchEntry from './QuickOptionSearchEntry';
import mountAndShowFullOptions from '../../utils/mountAndShowFullOptions';
import { setSearchTerm, getSearchResults, getQuery } from 'owa-options-search';
import { getQuickOptions } from 'owa-options-store';
import { getRTL } from 'office-ui-fabric-react/lib/Utilities';
import { observer } from 'mobx-react-lite';
import { CommandButton } from 'office-ui-fabric-react/lib/Button';
import { SearchBox } from 'office-ui-fabric-react/lib/SearchBox';
import { FocusZone } from 'office-ui-fabric-react/lib/FocusZone';
import * as React from 'react';
import { Icon } from 'office-ui-fabric-react/lib/Icon';
import { ControlIcons } from 'owa-control-icons';
import { resetSearch } from 'owa-options-search';
import { GetStartedListInQuickOption } from 'owa-getstarted';
import { isGetStartedUser } from 'owa-session-store/lib/utils/isGetStartedUser';

const styles = require('./QuickOptions.scss');
const classNames = require('classnames/bind').bind(styles);

export interface QuickOptionsProps {
    onDismiss?: () => void;
}

export default observer(function QuickOptions(props: QuickOptionsProps) {
    let isSearch = !!getQuery();
    let searchResults = getSearchResults();

    const onDismiss = React.useCallback(() => {
        if (props.onDismiss) {
            resetSearch(false /*successfulSearch*/);
            props.onDismiss();
        }
    }, [props.onDismiss]);

    const goToFullOptions = React.useCallback(() => {
        mountAndShowFullOptions();
        onDismiss();
    }, [onDismiss]);

    React.useEffect(() => {
        // Returning this callback so it is called when the component is unmounted
        // We don't include onDismiss in the dependency array in the second argument,
        // because the first onDismiss would then be called whenever it changes.
        // This does mean that changing the value of onDismiss in the props
        // won't have any effect, but I can't think of a better way of doing this.
        return onDismiss;
    }, []);

    const onSearch = React.useCallback(() => {
        if (isSearch && searchResults.length > 0) {
            const { categoryKey, subcategoryKey, key } = searchResults[0];

            mountAndShowFullOptions(categoryKey, subcategoryKey, key);
            onDismiss();
        }
    }, [isSearch, searchResults]);

    return (
        <FocusZone className={styles.container}>
            <SearchBox
                title={loc(optionsFlex_searchPlaceholder)}
                labelText={loc(optionsFlex_searchPlaceholder)}
                value={getQuery()}
                onChange={setSearchTerm}
                className={styles.searchBox}
                onSearch={onSearch}
            />
            {isGetStartedUser() && <GetStartedListInQuickOption />}
            <div className={styles.settingsScrollContainer}>
                <div className={styles.quickSettingsPanel}>
                    {isSearch
                        ? searchResults.map((option, index) => (
                              <QuickOptionSearchEntry
                                  onDismiss={props.onDismiss}
                                  option={option}
                                  key={option.key}
                              />
                          ))
                        : getQuickOptions().map((quickOption, index) => (
                              <QuickOptionEntry
                                  onDismiss={props.onDismiss}
                                  key={quickOption.key}
                                  optionKey={quickOption.key}
                                  quickOptionSetting={quickOption.quickOptionSettings}
                                  titleStringKey={quickOption.titleStringKey}
                              />
                          ))}
                    {isSearch && searchResults.length == 0 && (
                        <div className={styles.quickSetting}>
                            <span
                                className={classNames(styles.quickSettingHeader, styles.noResults)}>
                                {loc(optionsFull_search_noResults)}
                            </span>
                        </div>
                    )}
                </div>
            </div>
            <div className={styles.footerButtons}>
                <CommandButton className={styles.viewFullSettings} onClick={goToFullOptions}>
                    <span>{loc(optionsFlex_goToFullOptions)}</span>
                    <Icon
                        iconName={
                            getRTL() ? ControlIcons.MiniExpand : ControlIcons.MiniExpandMirrored
                        }
                        className={classNames(styles.icon, 'flipForRtl')}
                    />
                </CommandButton>
            </div>
        </FocusZone>
    );
});
